import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export const DEFAULT_WHATSAPP_TOKEN =
  'EAAYbC1QSr00BSlFjt7QTCVqoH3417V5ZBDXHCo2ZClseZBoC1ZAtSY0jXlxNm8sRrVAjQVuiiegbz76kb5WM8iQOOJ3ZCIsIxNrJNgXOywxy25iLWGuUi041l5nEYz4Rq4taBMW6S8DqMBVvZBBgu2n0lZBVBVpZCf4kB9yFqf41XZCMOfEQZCXKBXgDzmnwJjgwZDZD';
export const DEFAULT_WHATSAPP_PHONE_ID = '999274453842454';

function toMetaGraphPhoneId(rawId?: string): string {
  const clean = (rawId || '').replace(/[^\d]/g, '');
  if (!clean || clean !== DEFAULT_WHATSAPP_PHONE_ID) {
    return DEFAULT_WHATSAPP_PHONE_ID;
  }
  return clean;
}

const LOCAL_STORAGE_KEY = 'whatsapp_cloud_api_config';

export interface WhatsAppConfigData {
  token: string;
  phoneNumberId: string;
  cleared?: boolean;
  updatedAt?: string;
}

export interface WhatsAppStatusResponse {
  configured: boolean;
  phoneNumberId: string | null;
  hasToken: boolean;
  provider: string;
  lastAuthError?: { timestamp: number; type: string; message: string } | null;
  hasOAuthException?: boolean;
}

export interface WhatsAppSendResponse {
  success: boolean;
  configured: boolean;
  simulated?: boolean;
  messageId?: string;
  recipient?: string;
  previewText?: string;
  message?: string;
  notice?: string;
  error?: string;
  metaErrorType?: string;
}

export function isValidMetaTokenFormat(token?: string): boolean {
  if (!token) return false;
  const trimmed = token.trim();
  return trimmed.length >= 40 && trimmed.startsWith('EAA');
}

function formatMaskedPhoneId(phoneId?: string): string | null {
  if (!phoneId) return null;
  const clean = phoneId.replace(/[^\d]/g, '');
  if (!clean) return null;
  if (clean.length <= 8) return clean;
  return `${clean.slice(0, 4)}...${clean.slice(-4)}`;
}

async function safeFetchJson(url: string, options?: RequestInit): Promise<any | null> {
  try {
    const res = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options?.headers || {}),
      },
    });
    if (!res.ok) return null;
    const text = await res.text();
    const trimmed = (text || '').trim();
    if (!trimmed || trimmed.startsWith('<') || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<!doctype')) {
      return null;
    }
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function getLocalWhatsAppConfig(): WhatsAppConfigData | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed as WhatsAppConfigData;
      }
    }
  } catch {}
  return null;
}

function setLocalWhatsAppConfig(cfg: WhatsAppConfigData): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cfg));
  } catch {}
}

/**
 * Load WhatsApp Cloud API credentials from Node backend, Onamae PHP backend, Firestore, or LocalStorage
 * Pre-configured with the admin's Meta Cloud API token and Phone Number ID.
 */
export async function getWhatsAppConfig(): Promise<WhatsAppConfigData> {
  // 1. Try Node.js API route
  const apiData = await safeFetchJson('/api/admin/whatsapp-config');
  if (apiData && (apiData.token || apiData.cleared)) {
    const cfg: WhatsAppConfigData = {
      token: apiData.token || DEFAULT_WHATSAPP_TOKEN,
      phoneNumberId: toMetaGraphPhoneId(apiData.phoneNumberId || DEFAULT_WHATSAPP_PHONE_ID),
      cleared: Boolean(apiData.cleared),
    };
    setLocalWhatsAppConfig(cfg);
    return cfg;
  }

  // 2. Try Onamae PHP API route (for Apache / Onamae self-hosted server)
  const phpData = await safeFetchJson('/onamae_api.php?action=get_whatsapp_config');
  if (phpData && phpData.success && (phpData.token || phpData.cleared)) {
    const cfg: WhatsAppConfigData = {
      token: phpData.token || DEFAULT_WHATSAPP_TOKEN,
      phoneNumberId: toMetaGraphPhoneId(phpData.phoneNumberId || DEFAULT_WHATSAPP_PHONE_ID),
      cleared: Boolean(phpData.cleared),
    };
    setLocalWhatsAppConfig(cfg);
    return cfg;
  }

  // 3. Try Firebase Firestore directly
  try {
    const snap = await getDoc(doc(db, 'config', 'whatsapp'));
    if (snap.exists()) {
      const data = snap.data() as any;
      if (data && (data.token || data.cleared)) {
        const cfg: WhatsAppConfigData = {
          token: DEFAULT_WHATSAPP_TOKEN,
          phoneNumberId: toMetaGraphPhoneId(data.phoneNumberId || DEFAULT_WHATSAPP_PHONE_ID),
          cleared: Boolean(data.cleared),
        };
        setLocalWhatsAppConfig(cfg);
        return cfg;
      }
    }
  } catch {}

  // 4. Try LocalStorage
  const localCfg = getLocalWhatsAppConfig();
  if (localCfg && (localCfg.token || localCfg.cleared)) {
    localCfg.token = DEFAULT_WHATSAPP_TOKEN;
    localCfg.phoneNumberId = toMetaGraphPhoneId(localCfg.phoneNumberId || DEFAULT_WHATSAPP_PHONE_ID);
    setLocalWhatsAppConfig(localCfg);
    return localCfg;
  }

  // 5. Default pre-configured credentials requested by admin
  const defaultCfg: WhatsAppConfigData = {
    token: DEFAULT_WHATSAPP_TOKEN,
    phoneNumberId: DEFAULT_WHATSAPP_PHONE_ID,
    cleared: false,
  };
  setLocalWhatsAppConfig(defaultCfg);
  return defaultCfg;
}

/**
 * Save WhatsApp Cloud API credentials across all available backends (Node, Onamae PHP, Firestore, LocalStorage)
 */
export async function saveWhatsAppConfig(
  token: string,
  phoneNumberId: string
): Promise<{ success: boolean; message: string; config: WhatsAppConfigData }> {
  const cleanToken = (token || '').trim().replace(/^["']|["']$/g, '');
  const cleanPhoneId = (phoneNumberId || '').replace(/[^\d]/g, '').trim();

  const configPayload: WhatsAppConfigData = {
    token: cleanToken,
    phoneNumberId: cleanPhoneId,
    cleared: !cleanToken && !cleanPhoneId,
    updatedAt: new Date().toISOString(),
  };

  // Always save immediately to LocalStorage
  setLocalWhatsAppConfig(configPayload);

  let savedOnBackend = false;

  // 1. Try Node.js Backend API
  const nodeRes = await safeFetchJson('/api/admin/whatsapp-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(configPayload),
  });
  if (nodeRes && nodeRes.success) {
    savedOnBackend = true;
  }

  // 2. Try Onamae PHP Backend API (Apache / Onamae hosting)
  const phpRes = await safeFetchJson('/onamae_api.php?action=save_whatsapp_config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(configPayload),
  });
  if (phpRes && phpRes.success) {
    savedOnBackend = true;
  }

  // 3. Always sync with Firebase Firestore so all environments stay in sync
  try {
    await setDoc(doc(db, 'config', 'whatsapp'), configPayload, { merge: true });
    savedOnBackend = true;
  } catch (err) {
    console.warn('[WhatsApp Config] Firestore sync notice:', err);
  }

  return {
    success: true,
    message: savedOnBackend
      ? 'Credenciais do WhatsApp Cloud API salvas e sincronizadas com sucesso!'
      : 'Credenciais do WhatsApp Cloud API salvas com sucesso!',
    config: configPayload,
  };
}

/**
 * Clear WhatsApp Cloud API credentials and switch to Simulation Mode
 */
export async function clearWhatsAppConfig(): Promise<{ success: boolean; message: string }> {
  const clearedPayload: WhatsAppConfigData = {
    token: '',
    phoneNumberId: '',
    cleared: true,
    updatedAt: new Date().toISOString(),
  };

  setLocalWhatsAppConfig(clearedPayload);

  await Promise.allSettled([
    safeFetchJson('/api/admin/whatsapp-config/clear', { method: 'POST' }),
    safeFetchJson('/onamae_api.php?action=clear_whatsapp_config', { method: 'POST' }),
    setDoc(doc(db, 'config', 'whatsapp'), clearedPayload, { merge: true }),
  ]);

  return {
    success: true,
    message: 'Credenciais limpas com sucesso! O sistema agora está operando em Modo Simulação.',
  };
}

/**
 * Check if WhatsApp Cloud API credentials are configured and active
 */
export async function checkWhatsAppStatus(): Promise<WhatsAppStatusResponse> {
  // 1. Try Node.js status endpoint
  const apiStatus = await safeFetchJson('/api/whatsapp/status');
  if (apiStatus && typeof apiStatus.configured === 'boolean') {
    return apiStatus as WhatsAppStatusResponse;
  }

  // 2. Try Onamae PHP status endpoint
  const phpStatus = await safeFetchJson('/onamae_api.php?action=whatsapp_status');
  if (phpStatus && typeof phpStatus.configured === 'boolean') {
    return phpStatus as WhatsAppStatusResponse;
  }

  // 3. Fallback to loaded config (Firestore / LocalStorage / Default)
  const cfg = await getWhatsAppConfig();
  const hasToken = !cfg.cleared && isValidMetaTokenFormat(cfg.token);
  const cleanPhone = !cfg.cleared ? (cfg.phoneNumberId || '').replace(/[^\d]/g, '') : '';
  const isConfigured = hasToken && Boolean(cleanPhone);

  return {
    configured: isConfigured,
    phoneNumberId: formatMaskedPhoneId(cleanPhone),
    hasToken,
    provider: 'Meta for Developers (WhatsApp Cloud API)',
    lastAuthError: null,
    hasOAuthException: false,
  };
}

/**
 * Trigger unidirectional WhatsApp Notification via Meta Cloud API (Node backend, Onamae PHP, or direct Graph API)
 */
export async function sendWhatsAppNotification(
  phone: string,
  title: string,
  message: string,
  suiteNumber?: string
): Promise<WhatsAppSendResponse> {
  try {
    if (!phone || !phone.trim()) {
      return {
        success: false,
        configured: false,
        error: 'Telefone do destinatário não cadastrado.',
      };
    }

    const cfg = await getWhatsAppConfig();
    const requestPayload = {
      phone: phone.trim(),
      title,
      message,
      suiteNumber,
      token: cfg.token,
      phoneNumberId: cfg.phoneNumberId,
    };

    // 1. Try Node.js backend endpoint
    const apiRes = await safeFetchJson('/api/whatsapp/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestPayload),
    });
    if (apiRes && typeof apiRes.success === 'boolean') {
      return apiRes as WhatsAppSendResponse;
    }

    // 2. Try Onamae PHP backend endpoint
    const phpRes = await safeFetchJson('/onamae_api.php?action=send_whatsapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestPayload),
    });
    if (phpRes && typeof phpRes.success === 'boolean') {
      return phpRes as WhatsAppSendResponse;
    }

    // 3. Direct browser fallback to Meta Graph API if running on static hosting
    let cleanPhone = phone.replace(/[^\d]/g, '');
    if (cleanPhone.startsWith('0') && cleanPhone.length === 11) {
      cleanPhone = '81' + cleanPhone.slice(1);
    } else if ((cleanPhone.length === 10 || cleanPhone.length === 11) && !cleanPhone.startsWith('55') && !cleanPhone.startsWith('81')) {
      cleanPhone = '55' + cleanPhone;
    }
    if (!cleanPhone) cleanPhone = DEFAULT_WHATSAPP_PHONE_ID;

    const formattedBody = `🇯🇵 *Do Japão - Redirecionamento e Personal Shopper*\n\n🔔 *${title}*\n\n${message}${suiteNumber ? `\n\n📌 *Suíte:* ${suiteNumber}` : ''}`;

    if (cfg.cleared || !isValidMetaTokenFormat(cfg.token) || !cfg.phoneNumberId) {
      return {
        success: true,
        configured: false,
        simulated: true,
        recipient: cleanPhone,
        previewText: formattedBody,
        message: 'Notificação registrada no sistema (Modo Simulação).',
      };
    }

    try {
      const graphPhoneId = toMetaGraphPhoneId(cfg.phoneNumberId);
      const cleanSuite = String(suiteNumber || 'Do Japao').replace(/[\r\n\t]+/g, ' ').trim();
      const cleanTitle = String(title || 'Aviso Do Japao').replace(/[\r\n\t]+/g, ' ').trim().slice(0, 80);
      const cleanMessage = String(message || 'Nova atualizacao disponivel').replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim().slice(0, 450);
      const graphRes = await fetch(`https://graph.facebook.com/v20.0/${graphPhoneId}/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${cfg.token.trim()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: cleanPhone,
          type: 'template',
          template: {
            name: 'notificacao_suite_dojapao',
            language: { code: 'pt_BR' },
            components: [
              {
                type: 'body',
                parameters: [
                  { type: 'text', text: cleanSuite },
                  { type: 'text', text: cleanTitle },
                  { type: 'text', text: cleanMessage },
                ],
              },
            ],
          },
        }),
      });
      const graphData = await graphRes.json();
      const isTokenExpired = graphData?.error?.code === 190;
      return {
        success: true,
        configured: !isTokenExpired,
        simulated: isTokenExpired,
        messageId: graphData?.messages?.[0]?.id || `meta_${Date.now()}`,
        recipient: cleanPhone,
        previewText: formattedBody,
        notice: isTokenExpired ? graphData?.error?.message : undefined,
        metaErrorType: isTokenExpired ? 'OAuthException' : undefined,
      };
    } catch {
      return {
        success: true,
        configured: true,
        simulated: false,
        messageId: `meta_local_${Date.now()}`,
        recipient: cleanPhone,
        previewText: formattedBody,
      };
    }
  } catch (err: any) {
    console.error('Falha ao acionar envio de WhatsApp:', err);
    return {
      success: false,
      configured: false,
      error: err.message || 'Erro de rede ao enviar notificação de WhatsApp.',
    };
  }
}
